import { useMemo, useState } from "react";

import GameLauncher from "../shared/GameLauncher";
import GameProgress from "../shared/GameProgress";
import GameResults from "../shared/GameResults";
import GameInterpretation from "../shared/GameInterpretation.jsx";
import { getBasicInterpretation } from "../shared/gameInterpretation";

import GameFields from "../shared/GameFields";
import resolveGameConfig from "../shared/resolveGameConfig";

import {
  calculateProgress,
  calculateAverageReactionTime,
  calculateTotalTime,
  calculateSelectionAccuracy,
  calculateErrorCount,
} from "../shared/gameMetrics";

import "./SequenceCancellation.css";

const LETTERS = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
];

const GAME_CONFIG = {
  easy: {
    label: "Fácil",
    totalRounds: 8,
    itemCount: 6,
    targetCount: 2,
    patternLength: 2,
    columns: 3,
    similarDistractors: false,
  },

  medium: {
    label: "Media",
    totalRounds: 10,
    itemCount: 9,
    targetCount: 3,
    patternLength: 3,
    columns: 3,
    similarDistractors: true,
  },

  hard: {
    label: "Difícil",
    totalRounds: 12,
    itemCount: 12,
    targetCount: 4,
    patternLength: 3,
    columns: 4,
    similarDistractors: true,
  },
};

function randomItem(list) {
  return list[Math.floor(Math.random() * list.length)];
}

function shuffle(list) {
  const copy = [...list];

  for (let index = copy.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(
      Math.random() * (index + 1)
    );

    [copy[index], copy[randomIndex]] = [
      copy[randomIndex],
      copy[index],
    ];
  }

  return copy;
}

function createPattern(length) {
  return Array.from(
    { length },
    () => randomItem(LETTERS)
  ).join("");
}

function mutatePattern(pattern) {
  const characters = pattern.split("");

  const mutationIndex = Math.floor(
    Math.random() * characters.length
  );

  const availableLetters = LETTERS.filter(
    (letter) => letter !== characters[mutationIndex]
  );

  characters[mutationIndex] = randomItem(
    availableLetters
  );

  return characters.join("");
}

function reversePattern(pattern) {
  return pattern.split("").reverse().join("");
}

function rotatePattern(pattern) {
  if (pattern.length <= 1) {
    return pattern;
  }

  return `${pattern.slice(1)}${pattern[0]}`;
}

function createDistractor(
  targetPattern,
  similarDistractors,
  usedPatterns
) {
  let candidate = targetPattern;
  let attempts = 0;

  while (
    (
      candidate === targetPattern ||
      usedPatterns.has(candidate)
    ) &&
    attempts < 100
  ) {
    attempts += 1;

    if (similarDistractors) {
      const strategy = Math.floor(
        Math.random() * 3
      );

      if (strategy === 0) {
        candidate = mutatePattern(targetPattern);
      } else if (strategy === 1) {
        candidate = reversePattern(targetPattern);
      } else {
        candidate = rotatePattern(targetPattern);
      }

      /*
       * En secuencias como AA, invertir o rotar
       * puede devolver exactamente el modelo.
       */
      if (candidate === targetPattern) {
        candidate = mutatePattern(targetPattern);
      }
    } else {
      candidate = createPattern(targetPattern.length);
    }
  }

  if (
    candidate === targetPattern ||
    usedPatterns.has(candidate)
  ) {
    do {
      candidate = createPattern(targetPattern.length);
    } while (
      candidate === targetPattern ||
      usedPatterns.has(candidate)
    );
  }

  return candidate;
}

function createRound(config) {
  const safeTargetCount = Math.min(
    config.targetCount,
    config.itemCount - 1
  );

  const targetPattern = createPattern(
    config.patternLength
  );

  const distractorCount =
    config.itemCount - safeTargetCount;

  const usedPatterns = new Set([
    targetPattern,
  ]);

  const distractors = Array.from(
    { length: distractorCount },
    (_, index) => {
      const pattern = createDistractor(
        targetPattern,
        config.similarDistractors,
        usedPatterns
      );

      usedPatterns.add(pattern);

      return {
        id: `distractor-${index}`,
        pattern,
        isTarget: false,
      };
    }
  );

  const targets = Array.from(
    { length: safeTargetCount },
    (_, index) => ({
      id: `target-${index}`,
      pattern: targetPattern,
      isTarget: true,
    })
  );

  return {
    targetPattern,
    items: shuffle([
      ...targets,
      ...distractors,
    ]),
  };
}

export default function SequenceCancellation() {
  const [selectedDifficulty, setSelectedDifficulty] =
    useState(null);

  const [config, setConfig] = useState(null);

  const [roundIndex, setRoundIndex] = useState(0);
  const [round, setRound] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);

  const [correctRounds, setCorrectRounds] =
    useState(0);

  const [correctSelections, setCorrectSelections] =
    useState(0);

  const [wrongSelections, setWrongSelections] =
    useState(0);

  const [omissions, setOmissions] = useState(0);

  const [responses, setResponses] = useState([]);

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  const [roundStartTime, setRoundStartTime] =
    useState(null);

  const totalRounds = config?.totalRounds || 0;

  const progress = useMemo(
    () =>
      calculateProgress(
        roundIndex,
        totalRounds
      ),
    [roundIndex, totalRounds]
  );

  const totalErrors = calculateErrorCount(
    wrongSelections,
    omissions
  );

  const accuracy = calculateSelectionAccuracy({
    correctSelections,
    wrongSelections,
    omissions,
  });

  const averageReactionTime =
    calculateAverageReactionTime(responses);

  const totalTime = calculateTotalTime(
    startTime,
    endTime
  );

  const interpretation = finished
    ? getBasicInterpretation({
        accuracy,
        errors: totalErrors,
        averageReactionTime,
        difficulty: selectedDifficulty,
      })
    : null;

  const startGame = (
    difficultyOrConfig = selectedDifficulty || "easy"
  ) => {
    const {
      difficultyKey,
      config: nextConfig,
    } = resolveGameConfig(
      difficultyOrConfig,
      GAME_CONFIG
    );

    const now = Date.now();

    setSelectedDifficulty(difficultyKey);
    setConfig(nextConfig);

    setRoundIndex(0);
    setRound(createRound(nextConfig));
    setSelectedIds([]);

    setCorrectRounds(0);
    setCorrectSelections(0);
    setWrongSelections(0);
    setOmissions(0);
    setResponses([]);

    setStarted(true);
    setFinished(false);

    setStartTime(now);
    setEndTime(null);
    setRoundStartTime(now);
  };

  const toggleItem = (itemId) => {
    if (!started || finished) {
      return;
    }

    setSelectedIds((previous) =>
      previous.includes(itemId)
        ? previous.filter((id) => id !== itemId)
        : [...previous, itemId]
    );
  };

  const confirmRound = () => {
    if (
      !started ||
      finished ||
      !round ||
      !config
    ) {
      return;
    }

    const now = Date.now();

    const reactionTime =
      now - roundStartTime;

    const selectedItems = round.items.filter(
      (item) => selectedIds.includes(item.id)
    );

    const targetItems = round.items.filter(
      (item) => item.isTarget
    );

    const roundCorrectSelections =
      selectedItems.filter(
        (item) => item.isTarget
      ).length;

    const roundWrongSelections =
      selectedItems.filter(
        (item) => !item.isTarget
      ).length;

    const roundOmissions =
      targetItems.length -
      roundCorrectSelections;

    const isPerfectRound =
      roundWrongSelections === 0 &&
      roundOmissions === 0;

    setResponses((previous) => [
      ...previous,
      {
        round: roundIndex + 1,
        targetPattern: round.targetPattern,
        selectedPatterns: selectedItems.map(
          (item) => item.pattern
        ),
        correctSelections:
          roundCorrectSelections,
        wrongSelections:
          roundWrongSelections,
        omissions: roundOmissions,
        isCorrect: isPerfectRound,
        reactionTime,
      },
    ]);

    if (isPerfectRound) {
      setCorrectRounds(
        (previous) => previous + 1
      );
    }

    setCorrectSelections(
      (previous) =>
        previous + roundCorrectSelections
    );

    setWrongSelections(
      (previous) =>
        previous + roundWrongSelections
    );

    setOmissions(
      (previous) =>
        previous + roundOmissions
    );

    const nextRoundIndex = roundIndex + 1;

    if (nextRoundIndex >= config.totalRounds) {
      setStarted(false);
      setFinished(true);
      setEndTime(now);
      return;
    }

    setRoundIndex(nextRoundIndex);
    setRound(createRound(config));
    setSelectedIds([]);
    setRoundStartTime(now);
  };

  const backToLauncher = () => {
    setSelectedDifficulty(null);
    setConfig(null);

    setRoundIndex(0);
    setRound(null);
    setSelectedIds([]);

    setStarted(false);
    setFinished(false);

    setCorrectRounds(0);
    setCorrectSelections(0);
    setWrongSelections(0);
    setOmissions(0);
    setResponses([]);

    setStartTime(null);
    setEndTime(null);
    setRoundStartTime(null);
  };

  if (
    !selectedDifficulty &&
    !started &&
    !finished
  ) {
    return (
      <GameLauncher
        title="Cancelación de secuencias"
        description="Observa la secuencia modelo y selecciona todas las secuencias exactamente iguales."
        areas={[
          "Atención sostenida",
          "Atención selectiva",
          "Rastreo visual",
          "Discriminación visual",
        ]}
        defaultDifficulty="easy"
        customFields={[
          GameFields.totalRounds({
            max: 30,
            defaultValue: 10,
          }),

          GameFields.itemCount({
            label: "Cantidad de secuencias",
            min: 4,
            max: 30,
            defaultValue: 9,
          }),

          GameFields.targetCount({
            label: "Cantidad de secuencias iguales al modelo",
            min: 1,
            max: 10,
            defaultValue: 3,
            validate: (value, values) => {
              if (
                Number(value) >=
                Number(values.itemCount)
              ) {
                return "La cantidad de secuencias iguales debe ser menor que la cantidad total de secuencias.";
              }

              return "";
            },
          }),

          GameFields.patternLength({
            label: "Número de letras por secuencia",
            min: 2,
            max: 6,
            defaultValue: 3,
          }),

          GameFields.columns({
            min: 2,
            max: 6,
            defaultValue: 3,
          }),

          GameFields.similarDistractors({
            label: "Usar secuencias muy parecidas",
            help:
              "Los distractores cambian el orden o una sola letra respecto al modelo.",
          }),
        ]}
        onStart={startGame}
      />
    );
  }

  return (
    <div className="sequence-cancellation-page">
      <div className="sequence-cancellation-card">
        <header className="sequence-cancellation-header">
          <div>
            <h1>Cancelación de secuencias</h1>

            <p>
              Dificultad:{" "}
              <strong>{config?.label}</strong>
            </p>
          </div>

          <button
            type="button"
            onClick={() =>
              startGame(selectedDifficulty)
            }
          >
            Reiniciar
          </button>
        </header>

        {started && round && (
          <>
            <GameProgress
              label="Ronda"
              current={roundIndex + 1}
              total={totalRounds}
              progress={progress}
            />

            <section className="sequence-model-section">
              <span>SECUENCIA MODELO</span>

              <div className="sequence-model">
                {round.targetPattern}
              </div>
            </section>

            <section className="sequence-cancellation-instruction">
              <h2>
                Selecciona todas las secuencias iguales
              </h2>

              <p>
                Deben coincidir exactamente las letras y
                su orden.
              </p>
            </section>

            <section
              className="sequence-cancellation-grid"
              style={{
                gridTemplateColumns: `repeat(${config.columns}, 1fr)`,
              }}
            >
              {round.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`sequence-cancellation-item ${
                    selectedIds.includes(item.id)
                      ? "selected"
                      : ""
                  }`}
                  onClick={() =>
                    toggleItem(item.id)
                  }
                  aria-pressed={selectedIds.includes(
                    item.id
                  )}
                >
                  {item.pattern}
                </button>
              ))}
            </section>

            <button
              type="button"
              className="confirm-sequence-button"
              onClick={confirmRound}
              disabled={selectedIds.length === 0}
            >
              Confirmar selección
            </button>

            <section className="sequence-cancellation-score">
              <span>
                Rondas correctas: {correctRounds}
              </span>

              <span>
                Aciertos: {correctSelections}
              </span>

              <span>
                Errores: {wrongSelections}
              </span>

              <span>
                Omisiones: {omissions}
              </span>
            </section>
          </>
        )}

        {finished && (
          <>
            <GameResults
              results={[
                {
                  label: "Rondas correctas",
                  value: correctRounds,
                },
                {
                  label: "Aciertos",
                  value: correctSelections,
                },
                {
                  label: "Errores",
                  value: wrongSelections,
                },
                {
                  label: "Omisiones",
                  value: omissions,
                },
                {
                  label: "Precisión",
                  value: `${accuracy}%`,
                },
                {
                  label: "Tiempo medio",
                  value: `${averageReactionTime} ms`,
                },
                {
                  label: "Tiempo total",
                  value: `${totalTime}s`,
                },
              ]}
              onRepeat={() =>
                startGame(selectedDifficulty)
              }
              onChangeDifficulty={backToLauncher}
            />

            <GameInterpretation
              interpretation={interpretation}
            />
          </>
        )}
      </div>
    </div>
  );
}